/** Data-as-of / pull-heartbeat banner (spec §8 review #9). Mirrors the AMI
 *  as-of/staleness pattern: a quiet "data as of HH:MM" line normally, and a
 *  prominent STALE warning when the last CLEAN pull is older than the
 *  server-provided staleness threshold.
 *
 *  Freshness is driven ENTIRELY by the server's `stale` flag (which compares
 *  `last_success_at` against `staleness_threshold_minutes`); the UI only
 *  presents it. Timestamps shown here are `last_success_at` too — the ingest
 *  heartbeat bumps `last_run_at` on every poll including failed ones, so
 *  showing it would read "fresh" while the feed is failing. */

import { useOmsStatus } from '../../hooks/useOms.ts'
import { formatAgo, formatClock } from './omsFormat.tsx'

export function FreshnessBanner() {
  const { data: status, isLoading, isError } = useOmsStatus()

  if (isLoading) {
    return (
      <div className="text-xs text-gray-400 px-3 py-2">Checking data freshness…</div>
    )
  }

  if (isError || !status) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs">
        <span className="font-semibold">Freshness unknown</span>
        <span>— the OMS ingest heartbeat could not be read.</span>
      </div>
    )
  }

  const asOf = formatClock(status.last_success_at)
  const ago = formatAgo(status.last_success_at)

  if (status.stale) {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-md border border-red-400 bg-red-50 text-red-800 text-sm"
      >
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
          STALE OMS DATA
        </span>
        <span>
          {status.last_success_at ? (
            <>
              Last successful pull {ago} (as of {asOf}) — older than the{' '}
              {status.staleness_threshold_minutes}-minute threshold.
            </>
          ) : (
            <>No successful pull on record — the feed has never completed cleanly.</>
          )}{' '}
          Outage state may be out of date; verify before dispatching.
        </span>
        {status.files_quarantined ? (
          <span className="text-xs text-red-700">
            {status.files_quarantined} file(s) quarantined
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-md border border-gray-200 bg-white text-xs text-gray-600">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-green-500" />
        <span className="font-medium text-gray-700">Data as of {asOf}</span>
        <span className="text-gray-400">({ago})</span>
      </span>
      {status.last_event_time ? (
        <span className="text-gray-400">
          latest signal {formatAgo(status.last_event_time)}
        </span>
      ) : null}
      {status.files_ingested != null ? (
        <span className="text-gray-400">
          {status.files_ingested} file(s) ingested
        </span>
      ) : null}
      {status.aggregation_stale ? (
        <span className="inline-flex items-center gap-1.5 text-amber-700" title="The ingest feed may be fresh, but the aggregation rollup pass has not run recently.">
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          aggregation pass{' '}
          {status.aggregation_last_success_at
            ? `${formatAgo(status.aggregation_last_success_at)} — stale`
            : 'never run'}
        </span>
      ) : (
        <span className="text-gray-400">
          aggregation pass {formatAgo(status.aggregation_last_success_at)}
        </span>
      )}
    </div>
  )
}
